import { useEffect, useMemo, useRef, useState } from 'react';
import { detectAdTypeFromFilename } from '../../lib/detectAdTypeFromFilename';
import {
  defaultAssetLabel,
  type AssetFolderKind,
} from '../../lib/detectAssetLabelDefault';
import type { AdType, AssetLabel } from '../../types/campaign';
import { DriveAssetCard } from './DriveAssetCard';
import { DriveLightbox } from './DriveLightbox';
import type { DriveFileSummary } from './types';

// Renders a grid of Drive files alongside the AM's per-file labels. Owns
// the lightbox state. Label editing is controlled — the parent passes
// `value` (the persisted AssetLabel[]) and an `onChange` that fires whenever
// any label changes; the parent threads that into form state so the
// existing Campaign save flow persists everything in one shot (spec §5.3).

type Props = {
  files: DriveFileSummary[];
  value: AssetLabel[];
  // Optional in view mode (`readOnly=true`). When read-only, label edits
  // are blocked at the card level so onChange would never fire — the
  // grid still accepts the prop for API symmetry but a no-op is fine.
  onChange: (next: AssetLabel[]) => void;
  // Whether the listing came from cache. Used by the parent to decide if a
  // "Refresh" button should pulse; not rendered inside the grid.
  cached?: boolean;
  // P2c polish round 1 (Fix 2): view-only mode. When true, every card
  // renders its label as read-only text. The lightbox still opens on
  // thumbnail click and the orphan warning still surfaces.
  readOnly?: boolean;
  // Ad Preview Mockups Stage 1 §2. Opt-in: when true, each card surfaces
  // an AdTypeDropdown (edit) or AdTypeBadge (view). Default false to
  // preserve the legacy card layout for callers that don't yet need
  // typing — DriveAssetsBrowser passes this through from its own
  // showAdType flag, so a single switch at the browser flips the whole
  // grid.
  showAdType?: boolean;
  // Stage 1 §13.15. Optional callback fired once per folder-load with
  // the list of "missing" stubs (Drive files that don't yet have a
  // label entry in `value`). Parent fire-and-forgets to the
  // server-side stub endpoint. Only invoked when:
  //   - readOnly is false (no writes from view surfaces)
  //   - showAdType is true (no point auto-detecting on surfaces that
  //     don't surface the dropdown)
  //   - `files` is non-empty and the parent's value array is loaded
  // Re-render-safe: a useRef tracks the (folderFingerprint, fileIds)
  // combos already processed this session so a parent re-render
  // doesn't re-fire.
  onStubsDetected?: (stubs: AssetLabel[]) => void;
  // OrganicAssets spec §8. When set, auto-detected stubs carry a default
  // label text derived from the filename's extension ("Image - Local - " /
  // "Video - Organic - " …). Only Local + Organic asset browsers pass this;
  // Meta / Google / Other leave it undefined (no default text, '' label).
  labelKind?: AssetFolderKind;
};

function findLabel(value: AssetLabel[], driveFileId: string): string {
  return value.find((entry) => entry.driveFileId === driveFileId)?.label ?? '';
}

function findAdType(value: AssetLabel[], driveFileId: string): AdType | undefined {
  return value.find((entry) => entry.driveFileId === driveFileId)?.adType;
}

// Author the next label set: replace the entry for `driveFileId`, preserving
// every other (including any orphan entries the user might want to keep
// while they recover the file in Drive). When the new label is non-empty,
// upsert; when it's empty we keep the entry around with an empty label so
// the AM doesn't accidentally lose the binding before they save.
//
// Ad Preview Mockups Stage 1 §1.2 (amended in §13.1, §13.8, + the Organic
// Assets detection follow-up). adType auto-detection rules:
//   - CREATE (no prior entry): always run detection.
//   - UPDATE when existing.adType == null (undefined OR null): run
//     detection and apply IF detection returns non-null. The original
//     "never retroactive" rule was tightened in §13.1 to cover undefined
//     (legacy labels). §13.8 tightens further to cover explicit null too,
//     based on production reports that detection wasn't firing. Trade-
//     off: an AM who deliberately picks "—" on a file will have it
//     overridden on the next label edit. This is now stronger: detection
//     no longer returns null (every filename resolves to a token match or
//     the 'feed' default), so any null/undefined adType is ALWAYS resolved
//     to a concrete value on the next edit — the "—" pick is never sticky.
//     Accept it — the rare edge case loses to the common case of every
//     asset carrying a usable label.
//   - UPDATE when existing.adType is 'story' / 'feed': preserve. AM-set
//     classifications are never overridden by detection.
function upsertLabel(
  value: AssetLabel[],
  file: DriveFileSummary,
  nextLabel: string,
): AssetLabel[] {
  const existing = value.find((entry) => entry.driveFileId === file.id);
  if (!existing) {
    const detected = detectAdTypeFromFilename(file.name);
    return [
      ...value,
      {
        driveFileId: file.id,
        driveFileName: file.name,
        label: nextLabel,
        adType: detected,
      },
    ];
  }
  // existing branch
  return value.map((entry) => {
    if (entry.driveFileId !== file.id) return entry;
    const priorAdType = entry.adType;
    // == null covers BOTH undefined (legacy / never authored) and
    // null (AM-cleared or any code path that set null without a
    // deliberate manual choice). See doc-comment trade-off above.
    const shouldDetect = priorAdType == null;
    const detected = shouldDetect ? detectAdTypeFromFilename(file.name) : null;
    const nextAdType: AdType | undefined =
      shouldDetect && detected !== null ? detected : priorAdType;
    return {
      ...entry,
      driveFileName: file.name,
      label: nextLabel,
      adType: nextAdType,
    };
  });
}

// Per spec §2.1, the AM can override the auto-detected adType from the
// dropdown. Same upsert pattern as labels — if no entry exists yet, create
// one with the dropdown value (and an empty label, since the AM hasn't
// typed one yet). If an entry exists, swap adType in place.
function upsertAdType(
  value: AssetLabel[],
  file: DriveFileSummary,
  nextAdType: AdType,
): AssetLabel[] {
  const existing = value.find((entry) => entry.driveFileId === file.id);
  if (existing) {
    return value.map((entry) =>
      entry.driveFileId === file.id
        ? { ...entry, driveFileName: file.name, adType: nextAdType }
        : entry,
    );
  }
  return [
    ...value,
    {
      driveFileId: file.id,
      driveFileName: file.name,
      label: '',
      adType: nextAdType,
    },
  ];
}

export function DriveAssetGrid({
  files,
  value,
  onChange,
  readOnly,
  showAdType,
  onStubsDetected,
  labelKind,
}: Props) {
  // Index into `files` for the lightbox. null = closed. Indexing instead of
  // file-pointer so Prev/Next can iterate the live-files list directly;
  // orphan ghost cards aren't included in the rotation (they have no
  // valid Drive id to preview).
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);

  // Orphan label entries — labels that point at file ids no longer in the
  // listing. Defensive UI per the operator's Q1 answer ("UI defensively
  // handles 'file no longer in folder'"). Render these as ghost cards so
  // the AM can see what's missing and decide.
  const fileIdSet = useMemo(() => new Set(files.map((f) => f.id)), [files]);
  const orphans = useMemo(
    () => value.filter((entry) => !fileIdSet.has(entry.driveFileId)),
    [value, fileIdSet],
  );

  // Stage 1 §13.15. Compute "missing" stubs — Drive files in the
  // listing that don't yet have an entry in `value`. Each stub
  // carries the AM-facing fields with `label: ''` and an auto-
  // detected `adType` so the dropdown shows the detected value from
  // the moment the browser opens.
  //
  // displayValue = value ⊕ missingStubs is used purely for rendering
  // (dropdown reads from it). The parent's `value` is unchanged
  // until either:
  //   - the AM types into a label / picks a dropdown → upsertLabel /
  //     upsertAdType flows through onChange like before, AND
  //   - the parent fire-and-forgets onStubsDetected → the server-side
  //     stub-merge endpoint persists the same stubs to Firestore so
  //     they survive the next page load.
  const missingStubs = useMemo<AssetLabel[]>(() => {
    if (!showAdType) return [];
    const valueIds = new Set(value.map((v) => v.driveFileId));
    const stubs: AssetLabel[] = [];
    for (const file of files) {
      if (valueIds.has(file.id)) continue;
      stubs.push({
        driveFileId: file.id,
        driveFileName: file.name,
        // OrganicAssets §8.1: Local / Organic folders seed the default
        // label text from the file extension; other folders leave it ''.
        label: labelKind ? defaultAssetLabel(file.name, labelKind) : '',
        adType: detectAdTypeFromFilename(file.name),
      });
    }
    return stubs;
  }, [files, value, showAdType, labelKind]);

  const displayValue = useMemo<AssetLabel[]>(
    () => (missingStubs.length === 0 ? value : [...value, ...missingStubs]),
    [value, missingStubs],
  );

  // Once-per-session firing of onStubsDetected. The ref tracks the
  // combined fingerprint of (folder file id list × handler identity)
  // so a parent re-render with the same file list + same callback
  // doesn't refire; a fresh folder load (or a new callback identity)
  // does.
  //
  // Fingerprint = sorted file-id join. Sort makes it stable across
  // Drive listing ordering quirks. Handler identity uses a WeakSet
  // since the callback object may change across renders if the
  // parent doesn't memoise — keying on the actual function ensures
  // we don't accidentally suppress a legitimate re-fire when the
  // parent provides a different handler.
  const stubFiredRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    // OrganicAssets §8 fix: the gate is "did this surface opt in via
    // onStubsDetected", NOT "is it read-only". The old `if (readOnly) return`
    // meant the row-level Local/Organic stub-write only ran in the RowEditor's
    // edit mode — never on the default view (or the Build View), so labels
    // stayed []. View surfaces that shouldn't write simply don't pass
    // onStubsDetected (Build View Other/Google, Campaign detail), so dropping
    // the readOnly gate doesn't make them write. The Campaign Meta browser is
    // always edit mode, so its behaviour is unchanged.
    if (!showAdType) return;
    if (!onStubsDetected) return;
    if (missingStubs.length === 0) return;
    // Fingerprint includes file ids AND the stub-detected adTypes —
    // a Drive file rename that changes adType would otherwise be
    // suppressed by the fingerprint cache.
    const fingerprint = missingStubs
      .map((s) => `${s.driveFileId}:${s.adType ?? 'null'}`)
      .sort()
      .join('|');
    if (stubFiredRef.current.has(fingerprint)) {
      return;
    }
    stubFiredRef.current.add(fingerprint);
    onStubsDetected(missingStubs);
  }, [missingStubs, onStubsDetected, showAdType, labelKind, files.length, value.length, readOnly]);

  if (files.length === 0 && orphans.length === 0) {
    return (
      <p className="rounded border border-slate-200 bg-slate-50 px-3 py-4 text-sm text-slate-500">
        This folder has no files.
      </p>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {files.map((file, idx) => (
          <DriveAssetCard
            key={file.id}
            file={file}
            label={findLabel(value, file.id)}
            onLabelChange={(next) => onChange(upsertLabel(value, file, next))}
            onPreview={() => setPreviewIndex(idx)}
            readOnly={readOnly}
            // Stage 1 §13.15. Dropdown reads from displayValue (value
            // + missing stubs) so the auto-detected adType is visible
            // from the moment the folder loads. The underlying
            // onChange callbacks still operate on `value` so AM edits
            // flow through upsertLabel / upsertAdType normally — the
            // stubs surface visually now and persist server-side via
            // the fire-and-forget onStubsDetected callback in
            // parallel.
            adType={showAdType ? findAdType(displayValue, file.id) : undefined}
            onAdTypeChange={
              showAdType && !readOnly
                ? (next) => onChange(upsertAdType(value, file, next))
                : undefined
            }
          />
        ))}
        {orphans.map((entry) => (
          <DriveAssetCard
            key={`orphan-${entry.driveFileId}`}
            file={{
              id: entry.driveFileId,
              name: entry.driveFileName,
              mimeType: 'application/octet-stream',
              iconUrl: null,
              thumbnailUrl: null,
              webContentLink: null,
              webViewLink: '#',
              size: null,
              modifiedTime: '',
            }}
            label={entry.label}
            orphan
            readOnly={readOnly}
            adType={showAdType ? entry.adType : undefined}
            onAdTypeChange={
              showAdType && !readOnly
                ? (next) =>
                    onChange(
                      value.map((v) =>
                        v.driveFileId === entry.driveFileId
                          ? { ...v, adType: next }
                          : v,
                      ),
                    )
                : undefined
            }
            onLabelChange={(next) =>
              onChange(
                value.map((v) =>
                  v.driveFileId === entry.driveFileId ? { ...v, label: next } : v,
                ),
              )
            }
            onPreview={() => {
              // Orphan entries can't be previewed; this is a no-op so the
              // card behaves normally. Could surface a toast later if AMs
              // hit the thumbnail expecting something to happen.
            }}
          />
        ))}
      </div>

      {previewIndex !== null && (
        <DriveLightbox
          files={files}
          initialIndex={previewIndex}
          labels={value}
          onClose={() => setPreviewIndex(null)}
        />
      )}
    </>
  );
}
